// import dynamic from "next/dynamic";
// const Lottie = dynamic(() => import("lottie-react"), { ssr: false });
import Lottie from "lottie-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

import { toCdnUrl } from "../lib/cdn";
import { resolveImage } from "../lib/media";
import { truncateTo2Decimals } from "../services/frontend/pricing.service";
import useStore from "../store/search-store";
import Picture from "./Picture";
import SoldBadge from "./SoldBadge";
import * as animationData from "./shimmer.json";

const productStock = (product) => product?.quantity;

const StoreItem = ({
  usedForCollectionhome,
  product,
  toggleDrawer,
  search,
  customHeightWidth,
  variant,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [fallbackSet, setFallbackSet] = useState(false);
  const setShouldSearchReopen = useStore((state) => state.setShouldSearchReopen);
  const router = useRouter();

  const isSimilar = variant === "similar";
  const productTitle = product?.title;
  const salePrice = product?.salePrice;
  const maxPrice = product?.maxPrice;
  const photos = product?.photos || [];
  const firstPhoto = typeof photos[0] === "string" ? photos[0] : photos[0]?.src;
  const photoV2 = typeof photos[0] === "object" ? photos[0] : null;

  const getProductPrice = (price, mrp = "") => {
    if (!price) return;
    const priceClass = isSimilar ? "text-[13px] font-medium text-checkoutText" : "";
    const mrpClass = isSimilar
      ? "text-[13px] font-normal text-[#9CA3AF] line-through"
      : "text-sm font-normal line-through opacity-50";

    if (mrp) {
      const truncatedMrp = truncateTo2Decimals(mrp);
      return (
        <>
          <span
            className={priceClass}
          >{`${String.fromCharCode(0x20b9)}${price}`}</span>{" "}
          <span className={mrpClass}>{`${String.fromCharCode(0x20b9)}${truncatedMrp}`}</span>
        </>
      );
    }

    return (
      <span
        className={priceClass}
      >{`${String.fromCharCode(0x20b9)}${salePrice}`}</span>
    );
  };

  // media pipeline: compressed variants when a ready V2 entry exists (flag-gated), else today's URL.
  // Search thumbs are 70×70 — use the sidebar slot (256.avif) so we don't pull grid/orig sizes.
  const imageSlot = search ? "sidebar" : usedForCollectionhome ? "gridWide" : "grid";
  const resolved = resolveImage(firstPhoto, photoV2, imageSlot);

  const getImageUrl = () => (firstPhoto ? toCdnUrl(firstPhoto) : "");

  const checkStock = () => {
    const stock = productStock(product);
    if (typeof stock === "number") return stock > 0;

    return false;
  };

  const isFluidSquare = !isSimilar && !customHeightWidth && !usedForCollectionhome;
  const imageSizeClass = isSimilar
    ? "h-[156px] w-[117px]"
    : customHeightWidth
      ? customHeightWidth
      : usedForCollectionhome
        ? "h-[calc((var(--max-width)/1.5)-50px)] w-[calc((var(--max-width)/1.5)-50px)] max-w-none"
        : "h-full w-full";

  const itemWidthClass = isSimilar
    ? "w-[117px]"
    : usedForCollectionhome
      ? "mb-3 max-w-[calc((var(--max-width)/1.5)-50px)]"
      : "w-full";

  if (!product) return "";

  const handleProductClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    if (search) {
      setShouldSearchReopen(router.asPath);
    } else {
      setShouldSearchReopen(null);
    }
    if (toggleDrawer) toggleDrawer();
  };

  const productHref = `/products/${product.id}`;

  if (search) {
    const imageUrl = getImageUrl();
    return (
      <Link href={productHref} prefetch={false}>
        <a
          className="flex w-full min-w-0 cursor-pointer items-start gap-x-4 text-left no-underline"
          onClick={handleProductClick}
        >
          {firstPhoto ? (
            <span className="relative h-[70px] w-[70px] shrink-0 overflow-hidden rounded-lg border border-[#ECEFF1]">
              {!checkStock() ? <SoldBadge compact /> : null}
              {resolved.isV2 ? (
                <Picture
                  resolved={resolved}
                  alt={productTitle}
                  width={70}
                  height={70}
                  imgClassName="h-full w-full object-cover"
                />
              ) : (
                <img
                  src={imageUrl}
                  alt={productTitle}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    if (!fallbackSet && firstPhoto) {
                      e.target.src = firstPhoto;
                      setFallbackSet(true);
                    }
                  }}
                />
              )}
            </span>
          ) : (
            <span className="h-[70px] w-[70px] shrink-0 rounded-lg border border-[#ECEFF1]" />
          )}
          <span className="min-w-0">
            <span className="line-clamp-2 text-[14px] font-medium tracking-[0.75%] text-gray-900">
              {productTitle}
            </span>

            {salePrice ? (
              <span className="mt-1 block text-[14px] font-normal tracking-[-0.15px] text-gray-600">
                {`${String.fromCharCode(0x20b9)}${salePrice}`}
              </span>
            ) : null}
          </span>
        </a>
      </Link>
    );
  }

  return (
    <Link href={productHref} prefetch={false}>
      <a
        className={`flex w-full min-w-0 flex-col items-start justify-start overflow-hidden no-underline ${itemWidthClass}`}
        onClick={handleProductClick}
      >
        <div className="w-full min-w-0">
          <div>
            {firstPhoto ? (
              <div
                className={`relative overflow-hidden rounded-lg border border-[#ECEFF1] ${
                  isFluidSquare ? "aspect-square w-full" : ""
                } ${isSimilar ? imageSizeClass : ""}`}
              >
                {!checkStock() ? <SoldBadge /> : null}

                {!imageLoaded && (
                  <div className="pointer-events-none absolute inset-0">
                    <Lottie
                      className="h-full w-full"
                      animationData={animationData.default || animationData}
                      loop={true}
                      autoplay={true}
                      rendererSettings={{
                        preserveAspectRatio: "xMidYMid slice",
                      }}
                    />
                  </div>
                )}

                {resolved.isV2 ? (
                  <Picture
                    resolved={resolved}
                    alt={productTitle}
                    loading={isSimilar ? "eager" : "lazy"}
                    // A hidden, 0×0 image is never requested. Safari then never fires onLoad, so this
                    // style never clears and the Network panel stays empty. The shimmer sits on top.
                    style={imageLoaded ? { cursor: "pointer" } : undefined}
                    imgClassName={`rounded-lg object-cover ${imageSizeClass}`}
                    onLoad={() => {
                      setImageLoaded(true);
                    }}
                  />
                ) : (
                  <LazyLoadImage
                    style={imageLoaded ? { cursor: "pointer" } : undefined}
                    visibleByDefault={isSimilar}
                    onLoad={() => {
                      setImageLoaded(true);
                    }}
                    onError={(e) => {
                      if (!fallbackSet) {
                        e.target.src = `${firstPhoto}`;
                        setFallbackSet(true);
                      }
                    }}
                    className={`rounded-lg object-cover ${imageSizeClass}`}
                    src={getImageUrl()}
                    unoptimized="true"
                    objectfit="cover"
                    alt={productTitle}
                    effect={customHeightWidth ? "blur" : undefined}
                  />
                )}
              </div>
            ) : null}
          </div>

          <div className={`flex w-full min-w-0 flex-col ${isSimilar ? "mt-2" : "py-1.5"}`}>
            <div
              className={`min-w-0 cursor-pointer text-wrap ${
                isSimilar
                  ? "text-[13px] font-normal text-checkoutText"
                  : "mb-0.5 text-sm font-normal text-fontBackground opacity-70"
              }`}
            >
              {product && <div className="line-clamp-1 break-all">{productTitle}</div>}
            </div>

            <div
              className={`cursor-pointer ${
                isSimilar ? "" : "text-sm font-medium text-fontBackground"
              }`}
            >
              {product && getProductPrice(salePrice, maxPrice)}
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
};

export default StoreItem;
